import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { authHeader, getStoredUser, readApiJson } from '../api/auth';
import {
  calculateFortnightPortfolio, fortnightDates, FortnightPortfolio,
  HypotheticalBucketRow, FORTNIGHT_START_BANK,
  FORTNIGHT_BACK_STAKE, FORTNIGHT_LAY_LIABILITY
} from './fortnightHypothetical';
import './LiveBucketsFortnightStrip.css';

const money = (n: number): string => new Intl.NumberFormat('en-AU', {
  style: 'currency', currency: 'AUD', maximumFractionDigits: 0
}).format(n);
const pct = (n: number, showPlus = false): string =>
  (showPlus && n > 0 ? '+' : '') + n.toFixed(1) + '%';
const shortDate = (d: string): string =>
  new Date(d + 'T12:00:00Z').toLocaleDateString('en-AU', {
    timeZone: 'UTC', day: 'numeric', month: 'short'
  });

type Snapshot = {
  stats: FortnightPortfolio;
  from: string;
  to: string;
  checkedAt: string;
};

export default function LiveBucketsFortnightStrip() {
  const isAdmin = getStoredUser()?.role === 'admin';
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(isAdmin);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    let active: AbortController | null = null;
    const load = async () => {
      active?.abort();
      const controller = new AbortController();
      active = controller;
      const timeout = setTimeout(() => controller.abort(), 45000);
      try {
        const dates = fortnightDates();
        const authorization = authHeader();
        const params = new URLSearchParams({ bucket: '__live_by_time__', from: dates.from, to: dates.to });
        const res = await fetch(API_BASE_URL + '/api/strategy/bucket-bets?' + params.toString(), {
          cache: 'no-store', headers: authorization, signal: controller.signal
        });
        const payload = await readApiJson(res, authorization.Authorization);
        if (!Array.isArray(payload?.bets)) throw new Error('No bucket-history data returned');
        const rows = payload.bets as HypotheticalBucketRow[];
        const stats = calculateFortnightPortfolio(rows, dates);
        if (mounted) {
          setSnapshot({ stats, from: dates.from, to: dates.to, checkedAt: new Date().toISOString() });
          setError('');
        }
      } catch (e: any) {
        if (mounted) setError(e?.name === 'AbortError' ? '14-day history request timed out' : 'Hypothetical results unavailable');
      } finally {
        clearTimeout(timeout);
        if (mounted) setLoading(false);
      }
    };
    void load();
    const interval = setInterval(() => { if (!document.hidden) void load(); }, 5 * 60 * 1000);
    return () => { mounted = false; active?.abort(); clearInterval(interval); };
  }, [isAdmin, refresh]);

  if (!isAdmin) return null;
  const s = snapshot?.stats;
  const details = s ? [
    s.policyCount + ' LIVE policies with history in window',
    s.sourceRows + ' original rows; ' + s.duplicatesRemoved + ' overlapping selections removed',
    s.retrospectiveIncluded + ' included retrospective observations; ' + s.forwardIncluded + ' prospective/actual-source observations',
    s.pending + ' pending; ' + s.unpriced + ' unpriced; ' + s.missingStartTime + ' missing race start time',
    s.conflictingRows + ' contradictory settlement groups excluded; ' + s.unfunded + ' unfunded selections',
    s.voided + ' voids; ' + money(s.riskTurnover) + ' risk turnover',
    '10% modelled commission on positive returns. Race-off-order settlement approximation.',
    'This is not realised Betfair profit or an executable fill-price replay.'
  ].join(' · ') : '';
  const hasConcerns = Boolean(s && (s.retrospectiveIncluded || s.pending || s.missingStartTime || s.unpriced || s.conflictingRows || s.unfunded));

  return (
    <section className="lbf-strip" aria-label="Last 14 days — simulated current live bet buckets">
      <div className="lbf-lead">
        <strong>14D <span>hypothetical</span></strong>
        <small>LIVE buckets · {snapshot ? shortDate(snapshot.from) + '–' + shortDate(snapshot.to) : 'last 14 days'}</small>
      </div>
      {s && s.settledSelections > 0 ? (
        <div className="lbf-metrics" aria-live="polite">
          <div className="lbf-metric"><span>Sim bank</span><b>{money(s.bankEnd)}</b></div>
          <div className="lbf-metric"><span>Net P/L</span><b className={s.netProfit >= 0 ? 'lbf-positive' : 'lbf-negative'}>{s.netProfit > 0 ? '+' : ''}{money(s.netProfit)}</b></div>
          <div className="lbf-metric"><span>Bank return</span><b className={s.netProfit >= 0 ? 'lbf-positive' : 'lbf-negative'}>{pct(s.bankReturnPct, true)}</b></div>
          <div className="lbf-metric"><span>ROI on risk</span><b>{pct(s.riskReturnPct, true)}</b></div>
          <div className="lbf-metric"><span>Settled picks</span><b>{s.settledSelections}<small title={s.backBets + ' BACK, ' + s.layBets + ' LAY'}> ({s.backBets}B/{s.layBets}L)</small></b></div>
          <div className="lbf-metric"><span>Max drawdown</span><b className="lbf-dd">{pct(s.maxDrawdownPct)}</b></div>
        </div>
      ) : <div className="lbf-placeholder">{loading ? 'Calculating 14-day bucket results…' : error || 'No verified settled selections in the last 14 days'}</div>}
      <div className="lbf-note" title={details}>
        <span>{money(FORTNIGHT_START_BANK)} start · {money(FORTNIGHT_BACK_STAKE)} BACK / {money(FORTNIGHT_LAY_LIABILITY)} LAY risk</span>
        {hasConcerns && <small>{s!.retrospectiveIncluded} retrospective · {s!.pending + s!.unpriced + s!.missingStartTime + s!.conflictingRows} unresolved/excluded</small>}
        {error && <small role="status">{error}</small>}
      </div>
      <div className="lbf-actions">
        <Link to="/strategy/bucket-bets" title="Inspect source buckets and their backtest or forward evidence">Buckets</Link>
        <button type="button" onClick={() => { setLoading(true); setRefresh(n => n + 1); }} disabled={loading}
          aria-label="Refresh 14-day hypothetical" title={snapshot ? 'Updated ' + new Date(snapshot.checkedAt).toLocaleTimeString('en-AU') + '. Refresh historical simulation.' : 'Refresh historical simulation'}>
          ↻
        </button>
      </div>
    </section>
  );
}
